import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createColumnHelper } from "@tanstack/react-table";
import { api } from "@/lib/api";
import { usePaged } from "@/lib/hooks";
import { useEntityAction } from "@/lib/mutations";
import { useAuth } from "@/auth/auth";
import { PagedTable } from "@/components/PagedTable";
import { RowActions } from "@/components/RowActions";
import { Button, StatusBadge } from "@/components/ui";
import { CustomerForm } from "@/components/CustomerForm";
import type { Customer } from "@/lib/types";

const col = createColumnHelper<Customer>();

export default function CustomersPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const canWrite = can("customers:write");
  const [q, setQ] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const create = useEntityAction([["customers"]], { inlineError: true, onSuccess: () => setShowNew(false) });
  const paged = usePaged<Customer>(["customers"], "/customers", {
    q: q || undefined,
    include_deleted: showDeleted ? "true" : undefined,
  });
  const action = useEntityAction([["customers"]]);

  const columns = useMemo(
    () => [
      col.accessor("full_name", {
        header: () => t("fields.name"),
        cell: (c) => (
          <Link
            className="font-medium text-sand hover:text-gold"
            to={`/customers/${c.row.original.id}`}
          >
            {c.getValue()}
          </Link>
        ),
      }),
      col.accessor("company", {
        header: () => t("fields.company"),
        cell: (c) => c.getValue() ?? "—",
      }),
      col.accessor("country", {
        header: () => t("fields.country"),
        cell: (c) => c.getValue() ?? "—",
      }),
      col.accessor("tags", {
        header: () => t("fields.tags"),
        cell: (c) => c.getValue().join(", ") || "—",
      }),
      col.accessor("status", {
        header: () => t("fields.status"),
        cell: (c) => <StatusBadge status={c.getValue()} />,
      }),
      ...(canWrite
        ? [
            col.display({
              id: "actions",
              header: () => t("common.actions"),
              cell: (c) => {
                const id = c.row.original.id;
                return (
                  <RowActions
                    deleted={!!c.row.original.deleted_at}
                    canWrite={canWrite}
                    busy={action.isPending}
                    onDelete={() => action.mutate(() => api.del(`/customers/${id}`))}
                    onRestore={() =>
                      action.mutate(() => api.post(`/customers/${id}/restore`))
                    }
                  />
                );
              },
            }),
          ]
        : []),
    ],
    [t, canWrite, action],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">{t("customers.title")}</h1>
        {canWrite && !showNew && (
          <Button className="px-3 py-1.5 text-xs" onClick={() => setShowNew(true)}>
            + {t("customers.new")}
          </Button>
        )}
      </div>
      {showNew && (
        <CustomerForm
          busy={create.isPending}
          error={create.isError ? (create.error as Error).message : null}
          submitLabel={t("common.create")}
          onCancel={() => setShowNew(false)}
          onSubmit={(payload) =>
            create.mutate(() => api.post<Customer>("/customers", payload), {
              onSuccess: (c) => navigate(`/customers/${(c as Customer).id}`),
            })
          }
        />
      )}
      <PagedTable
        columns={columns}
        data={paged.items}
        query={paged.query}
        total={paged.total}
        page={paged.page}
        pageCount={paged.pageCount}
        onPage={paged.setPage}
        empty={t("customers.empty")}
        toolbar={
          <div className="flex items-center gap-3">
            <input
              className="w-64 rounded-md border border-olive-600 bg-olive-800 px-3 py-1.5 text-sm text-sand placeholder:text-sand-muted focus:border-gold focus:outline-none"
              placeholder={t("customers.searchPlaceholder")}
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                paged.resetPage();
              }}
            />
            <label className="flex items-center gap-1.5 text-sm text-sand-muted">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => {
                  setShowDeleted(e.target.checked);
                  paged.resetPage();
                }}
              />
              {t("common.showDeleted")}
            </label>
          </div>
        }
      />
    </div>
  );
}
